import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { EMAIL_CONSTRAINT as getEmailFormatError } from 'bundles/authentication-common/constants/constraints';
import { useEnterpriseProgramsByOrganizationSlug } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseProgramsByOrganizationSlug';
import { useSystemProgram } from 'bundles/enterprise-admin-hooks/hooks/useSystemProgram';
import ProgramInvitationsDomainAllowListMultiDeleteMutationGql from 'bundles/enterprise-admin-program-creation/queries/ProgramInvitationsDomainAllowListMultiDeleteMutation.graphql';
import ProgramInvitationsDomainAllowListMultiUpdateMutationGql from 'bundles/enterprise-admin-program-creation/queries/ProgramInvitationsDomainAllowListMultiUpdateMutation.graphql';
import type {
  MultiDeleteMutation,
  MultiDeleteMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/ProgramInvitationsDomainAllowListMultiDeleteMutation';
import type {
  MultiUpdateMutation,
  MultiUpdateMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/ProgramInvitationsDomainAllowListMultiUpdateMutation';
import {
  type EnterpriseUserProgramAllowListQueryResults,
  type EnterpriseUserProgramAllowListQueryVariables,
  enterpriseUserProgramAllowListQuery,
} from 'bundles/enterprise-admin-program-users/utils/EnterpriseUserProgramAllowListQuery';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-learners';

import DomainAllowlistRostersGql from '../queries/DomainAllowlistRosters.graphql';
import type {
  DomainAllowlistRostersQuery,
  DomainAllowlistRostersQueryVariables,
} from '../queries/__generated__/DomainAllowlistRosters';

/** SelectField sentinel for "No group – Add to All learners" (targets the org's System Program). */
const NO_GROUP_VALUE = '';

type UseDomainAllowlistArgs = {
  /** True while the modal is open — resets all local state when it closes. */
  open: boolean;
  /** Set when the group step should be skipped (Group Details, or a Group admin with exactly
   * one group) — pre-selects and locks the group picker, mirroring EmailInviteModal/CsvInviteModal. */
  autoSelectedGroupId?: string;
};

/**
 * Group-scoped domain allowlisting (ENTADMIN-1956). A "group" is just an `EnterpriseProgram`;
 * "No group" targets the org's System Program.
 */
export const useDomainAllowlist = ({ open, autoSelectedGroupId }: UseDomainAllowlistArgs) => {
  const org = useThirdPartyOrganization();
  const { programs, loading: programsLoading } = useEnterpriseProgramsByOrganizationSlug(org.slug);
  const groupOptions = programs ?? [];
  const { systemProgram, loading: systemProgramLoading } = useSystemProgram(org.id);

  const [selectedGroupId, setSelectedGroupId] = React.useState(autoSelectedGroupId ?? NO_GROUP_VALUE);
  const [domainInputText, setDomainInputText] = React.useState('');
  const [domainInputError, setDomainInputError] = React.useState<string | undefined>(undefined);
  const [mutationError, setMutationError] = React.useState<string | undefined>(undefined);
  const [pendingDeleteDomain, setPendingDeleteDomain] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!open) {
      setDomainInputText('');
      setDomainInputError(undefined);
      setMutationError(undefined);
      setPendingDeleteDomain(undefined);
      return;
    }
    setSelectedGroupId(autoSelectedGroupId ?? NO_GROUP_VALUE);
  }, [open, autoSelectedGroupId]);

  const isGroupSelectionLocked = Boolean(autoSelectedGroupId);
  const showGroupPicker = groupOptions.length > 0 || isGroupSelectionLocked;
  const selectedGroup = groupOptions.find((group) => group.id === selectedGroupId);
  const targetProgramId = selectedGroup ? selectedGroup.id : systemProgram?.id;
  const targetProgramSlug = selectedGroup ? selectedGroup.slug : systemProgram?.slug;
  const targetGroupLabel = selectedGroup ? selectedGroup.name : _t('All learners');

  const {
    data: rosterData,
    loading: rosterLoading,
    error: rosterError,
  } = useQuery<DomainAllowlistRostersQuery, DomainAllowlistRostersQueryVariables>(DomainAllowlistRostersGql, {
    variables: { programIds: targetProgramId ? [targetProgramId] : [] },
    skip: !targetProgramId,
    context: { clientName: 'gatewayGql' },
  });
  const rosterId =
    rosterData?.EnterpriseRosters?.queryRostersByProgramIdsOrContractIdsOrBothAndIncludeDeleted?.elements[0]?.id;
  const hasRosterError = !rosterLoading && Boolean(rosterError);
  const hasNoRoster = !rosterLoading && !hasRosterError && Boolean(targetProgramId) && !rosterId;

  const {
    data: allowlistData,
    loading: allowlistLoading,
    refetch: refetchAllowlist,
  } = useQuery<EnterpriseUserProgramAllowListQueryResults, EnterpriseUserProgramAllowListQueryVariables>(
    enterpriseUserProgramAllowListQuery,
    {
      variables: { programId: targetProgramId ?? '', rosterIds: [] },
      skip: !targetProgramId,
      context: { clientName: 'gatewayGql' },
    }
  );

  const allowlist = [...(allowlistData?.ProgramInvitationsDomainAllowList?.queryDomainAllowList ?? [])];

  const [multiUpdateDomains, { loading: isAddingDomains }] = useMutation<
    MultiUpdateMutation,
    MultiUpdateMutationVariables
  >(ProgramInvitationsDomainAllowListMultiUpdateMutationGql, { context: { clientName: 'gatewayGql' } });
  const [multiDeleteDomains, { loading: isRemovingDomain }] = useMutation<
    MultiDeleteMutation,
    MultiDeleteMutationVariables
  >(ProgramInvitationsDomainAllowListMultiDeleteMutationGql, { context: { clientName: 'gatewayGql' } });

  const hasInvalidDomains = (domains: string[]) =>
    domains.some((domain) => Boolean(getEmailFormatError(`prefix@${domain}`)));

  const hasDuplicateDomains = (domains: string[]) =>
    domains.some(
      (domain, index) => allowlist.some((entry) => entry.emailDomain === domain) || domains.indexOf(domain) !== index
    );

  const onSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    setDomainInputError(undefined);
    setMutationError(undefined);
  };

  const onDomainInputTextChange = (text: string) => {
    setDomainInputText(text);
    setDomainInputError(undefined);
  };

  const addDomains = async () => {
    setDomainInputError(undefined);
    setMutationError(undefined);

    const domains = domainInputText
      .split(',')
      .map((domain) => domain.trim().toLowerCase())
      .filter(Boolean);
    if (domains.length === 0) return;

    if (hasInvalidDomains(domains)) {
      setDomainInputError(_t('Enter a valid domain name, like example.com.'));
      return;
    }
    if (hasDuplicateDomains(domains)) {
      setDomainInputError(_t('This domain has already been added.'));
      return;
    }
    if (!targetProgramId || !rosterId || hasRosterError) return;

    try {
      const response = await multiUpdateDomains({
        variables: { input: { programId: targetProgramId, emailDomains: domains, rosterId, insertOnly: false } },
      });
      if (response.errors) {
        setMutationError(_t('Something went wrong while adding your domain(s). Please try again.'));
        return;
      }
      await refetchAllowlist();
      setDomainInputText('');
    } catch {
      setMutationError(_t('Something went wrong while adding your domain(s). Please try again.'));
    }
  };

  const requestRemoveDomain = (domain: string) => setPendingDeleteDomain(domain);
  const cancelRemoveDomain = () => setPendingDeleteDomain(undefined);

  const confirmRemoveDomain = async () => {
    if (!pendingDeleteDomain || !targetProgramId || isRemovingDomain) return;
    setMutationError(undefined);
    try {
      const response = await multiDeleteDomains({
        variables: { input: { programId: targetProgramId, emailDomains: [pendingDeleteDomain] } },
      });
      if (response.errors) {
        setMutationError(_t('Something went wrong while removing this domain. Please try again.'));
        return;
      }
      await refetchAllowlist();
    } catch {
      setMutationError(_t('Something went wrong while removing this domain. Please try again.'));
    } finally {
      setPendingDeleteDomain(undefined);
    }
  };

  return {
    showGroupPicker,
    isGroupSelectionLocked,
    groupOptions,
    selectedGroupId,
    onSelectGroup,
    targetGroupLabel,
    targetProgramSlug,
    allowlist,
    canSave: allowlist.length > 0 && !isAddingDomains,
    loading: programsLoading || systemProgramLoading || rosterLoading || allowlistLoading,
    hasNoRoster,
    hasRosterError,
    domainInputText,
    onDomainInputTextChange,
    domainInputError,
    mutationError,
    addDomains,
    isAddingDomains,
    pendingDeleteDomain,
    requestRemoveDomain,
    cancelRemoveDomain,
    confirmRemoveDomain,
    isRemovingDomain,
  };
};
